/**
 * CodeExport Component
 * Export macros in various formats with copy functionality
 */

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Code, Copy, Check, FileCode, Link, Settings } from 'lucide-react'
import { Macro, MacroTestData, ExportFormat } from '../../types/macros'
import { generateMacroSyntax, generateSDKPayload, generateURLParams } from '../../utils/macroHelpers'
import { useToast } from '../ui/ToastProvider'
import EmptyStateIllustration from '../ui/EmptyStateIllustration'

interface CodeExportProps {
  macros: Macro[]
  testData: MacroTestData
}

const CodeExport: React.FC<CodeExportProps> = ({ macros, testData }) => {
  const [activeTab, setActiveTab] = useState<ExportFormat>(ExportFormat.MACRO_SYNTAX)
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const [copiedAll, setCopiedAll] = useState(false)
  const { showSuccess } = useToast()

  const handleCopy = async (text: string, index?: number) => {
    try {
      await navigator.clipboard.writeText(text)
      if (index !== undefined) {
        setCopiedIndex(index)
        setTimeout(() => setCopiedIndex(null), 2000)
      } else {
        setCopiedAll(true)
        setTimeout(() => setCopiedAll(false), 2000)
      }
      showSuccess('Copied to clipboard!')
    } catch (error) {
      console.error('Failed to copy:', error)
      showSuccess('Failed to copy', 'Error')
    }
  }

  const handleCopyAll = () => {
    if (activeTab === ExportFormat.MACRO_SYNTAX) {
      const allSyntax = macros.map(m => generateMacroSyntax(m)).join('\n')
      handleCopy(allSyntax)
    } else if (activeTab === ExportFormat.SDK_PAYLOAD) {
      const sdkCode = generateSDKPayload(macros, testData)
      handleCopy(sdkCode)
    } else if (activeTab === ExportFormat.URL_PARAMS) {
      const urlParams = generateURLParams(macros, testData)
      handleCopy(urlParams)
    }
  }

  const tabs = [
    {
      id: ExportFormat.MACRO_SYNTAX,
      label: 'Macro Syntax',
      icon: Code,
      description: 'Raw macro syntax for dashboard'
    },
    {
      id: ExportFormat.SDK_PAYLOAD,
      label: 'SDK Payload',
      icon: Settings,
      description: 'JavaScript SDK configuration'
    },
    {
      id: ExportFormat.URL_PARAMS,
      label: 'URL Parameters',
      icon: Link,
      description: 'Query parameters for direct links'
    }
  ]

  if (macros.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <h3 className="text-xl font-bold text-[#1A2B49]">Export Code</h3>
            <FileCode className="w-5 h-5 text-[#FE4215]" />
          </div>
        </div>
        <div className="py-8">
          <EmptyStateIllustration
            type="body"
            title="No Macros to Export"
            description="Add macros from the builder to generate export code"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <h3 className="text-xl font-bold text-[#1A2B49]">Export Code</h3>
          <FileCode className="w-5 h-5 text-[#FE4215]" />
        </div>
        <button
          onClick={handleCopyAll}
          className="flex items-center space-x-2 px-4 py-2 bg-[#233D71] text-white rounded-lg hover:bg-[#1A2B49] transition-all duration-200 text-sm font-medium"
        >
          {copiedAll ? (
            <>
              <Check className="w-4 h-4" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              <span>Copy All</span>
            </>
          )}
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-4">
          {tabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#FE4215] text-[#FE4215]'
                    : 'border-transparent text-[#6E6D7A] hover:text-[#1A2B49] hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Description */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-900">
          {tabs.find(t => t.id === activeTab)?.description}
        </p>
      </div>

      {/* Tab Content */}
      <div className="space-y-4">
        {/* Macro Syntax Tab */}
        {activeTab === ExportFormat.MACRO_SYNTAX && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
          >
            <p className="text-sm text-[#6E6D7A] mb-4">
              Copy these macro syntaxes and paste them into your MomentScience theme customizer in the dashboard.
            </p>
            {macros.map((macro, index) => {
              const syntax = generateMacroSyntax(macro)
              return (
                <div key={macro.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-semibold text-gray-500">#{index + 1}</span>
                      <code className="text-xs font-mono text-[#233D71]">{macro.key}</code>
                      {macro.description && (
                        <span className="text-xs text-[#6E6D7A]">- {macro.description}</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleCopy(syntax, index)}
                      className="flex items-center space-x-1 px-2 py-1 text-xs text-[#233D71] hover:bg-gray-200 rounded transition-colors"
                    >
                      {copiedIndex === index ? (
                        <>
                          <Check className="w-3 h-3" />
                          <span>Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="p-4 bg-gray-900">
                    <pre className="text-sm text-green-400 font-mono overflow-x-auto">
                      {syntax}
                    </pre>
                  </div>
                </div>
              )
            })}
          </motion.div>
        )}

        {/* SDK Payload Tab */}
        {activeTab === ExportFormat.SDK_PAYLOAD && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-sm text-[#6E6D7A] mb-4">
              Add this JavaScript code to your page before initializing the MomentScience SDK. Replace the sample values with actual customer data.
            </p>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b border-gray-200">
                <span className="text-xs font-semibold text-[#1A2B49]">JavaScript SDK Configuration</span>
                <button
                  onClick={() => handleCopy(generateSDKPayload(macros, testData))}
                  className="flex items-center space-x-1 px-2 py-1 text-xs text-[#233D71] hover:bg-gray-200 rounded transition-colors"
                >
                  {copiedAll ? (
                    <>
                      <Check className="w-3 h-3" />
                      <span>Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
              <div className="p-4 bg-gray-900 overflow-x-auto">
                <pre className="text-sm font-mono">
                  <code className="text-blue-400">window</code>
                  <code className="text-white">.</code>
                  <code className="text-blue-400">AdpxUser</code>
                  <code className="text-white"> = {`{\n`}</code>
                  {Array.from(new Set(macros.map(m => m.key))).map((key, index, arr) => (
                    <code key={key} className="block pl-4">
                      <span className="text-green-400">{key}</span>
                      <span className="text-white">: </span>
                      <span className="text-yellow-400">
                        "{testData[key] || `your_${key}_here`}"
                      </span>
                      {index < arr.length - 1 && <span className="text-white">,</span>}
                      <span className="text-white">{'\n'}</span>
                    </code>
                  ))}
                  <code className="text-white">{`};`}</code>
                </pre>
              </div>
            </div>

            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs text-yellow-900">
                <strong>Important:</strong> Make sure to replace the sample values with actual customer data from your system before deploying to production.
              </p>
            </div>
          </motion.div>
        )}

        {/* URL Parameters Tab */}
        {activeTab === ExportFormat.URL_PARAMS && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-sm text-[#6E6D7A] mb-4">
              Append these URL parameters to your Perkswall direct link to pass macro values dynamically.
            </p>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b border-gray-200">
                <span className="text-xs font-semibold text-[#1A2B49]">URL Query Parameters</span>
                <button
                  onClick={() => handleCopy(generateURLParams(macros, testData))}
                  className="flex items-center space-x-1 px-2 py-1 text-xs text-[#233D71] hover:bg-gray-200 rounded transition-colors"
                >
                  {copiedAll ? (
                    <>
                      <Check className="w-3 h-3" />
                      <span>Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
              <div className="p-4 bg-gray-900 overflow-x-auto">
                <pre className="text-sm text-green-400 font-mono break-all">
                  {generateURLParams(macros, testData)}
                </pre>
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-900 mb-2">
                <strong>Example URL:</strong>
              </p>
              <code className="text-xs bg-white px-2 py-1 rounded border border-blue-300 break-all block">
                https://naojs.pubtailer.com/perkswall?accountId=YOUR_ACCOUNT&themeId=YOUR_THEME{generateURLParams(macros, testData).replace('?', '&')}
              </code>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default CodeExport
